import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { log } from '../utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import { sessionManager } from '../utils/sessionManager';

/**
 * Custom hook for managing authentication state and session handling
 * @returns {Object} Authentication state and handlers
 */
export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentView, setCurrentView] = useState('login');
  const [isLoading, setIsLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isProcessingEmailConfirmation, setIsProcessingEmailConfirmation] = useState(false);
  const queryClient = useQueryClient();

  // Helper function to clear stale cache and trigger fresh data fetching
  const clearStaleCache = useCallback(() => {
    log.info('🗑️ [v4] Clearing potentially stale cache data');
    
    // Clear all cached data to prevent showing stale information
    queryClient.clear();
    
    // Force garbage collection of queries
    queryClient.getQueryCache().clear();
  }, [queryClient]);

  // Helper function to process user session data
  const processUserSession = useCallback(async (session, existingUser = null) => {
    if (!session?.user) return null;

    log.info('🔄 [v5] === processUserSession CALLED === for user:', session.user.email);
    log.info('🔄 [v5] Call stack:', new Error().stack);

    try {
      // Fetch user data from database - try by ID first, then by email
      log.info('📊 [v5] Fetching user data for ID:', session.user.id);

      let userData, userError;

      // Query user data from database
      log.info('📊 [v5] Starting database query for user ID:', session.user.id);
      const result = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single();

      log.info('📊 [v5] Query returned:', { hasData: !!result.data, hasError: !!result.error, errorCode: result.error?.code });

      userData = result.data;
      userError = result.error;

      if (userError && userError.code === 'PGRST116') {
        log.info('🔍 [v5] User not found by ID, trying by email...');
        const { data: userByEmail, error: emailError } = await supabase
          .from('users')
          .select('*')
          .eq('email', session.user.email)
          .single();

        userData = userByEmail;
        userError = emailError;
      }

      // If user doesn't exist in database, create them
      if (userError && userError.code === 'PGRST116') {
        log.info('👤 [v5] Creating new user in database');

        const { data: createdUser, error: createError } = await supabase
          .from('users')
          .insert({
            id: session.user.id,
            email: session.user.email,
            first_name: session.user.user_metadata?.first_name || '',
            last_name: session.user.user_metadata?.last_name || '',
            phone: session.user.user_metadata?.phone || '',
            role: 'team_manager', // Default role
            is_active: true
          })
          .select()
          .single();

        if (createError) {
          log.error('❌ [v5] Error creating/updating user:', createError);
          // If we have existing user data, keep it; otherwise fall back to session.user
          const fallbackUser = existingUser || session.user;
          setCurrentUser(fallbackUser);
          return fallbackUser;
        } else {
          log.info('✅ [v5] User created successfully');
          // Combine user data with specific auth properties (don't overwrite role)
          const combinedUser = {
            ...createdUser,
            // Only include specific auth properties that don't conflict with database
            email_confirmed_at: session.user.email_confirmed_at,
            user_metadata: session.user.user_metadata,
            app_metadata: session.user.app_metadata,
            // Keep the database role, don't use session.user.role which is just "authenticated"
          };
          setCurrentUser(combinedUser);
          return combinedUser;
        }
      } else if (userError) {
        log.error('❌ [v5] Error fetching user data:', userError);
        // If we have existing user data, keep it; otherwise fall back to session.user
        const fallbackUser = existingUser || session.user;
        setCurrentUser(fallbackUser);
        return fallbackUser;
      } else {
        log.info('✅ [v5] User data fetched successfully');
        // Combine user data with specific auth properties (don't overwrite role)
        const combinedUser = { 
          ...userData, 
          // Only include specific auth properties that don't conflict with database
          email_confirmed_at: session.user.email_confirmed_at,
          user_metadata: session.user.user_metadata,
          app_metadata: session.user.app_metadata,
          // Keep the database role, don't use session.user.role which is just "authenticated"
        };
        log.info('🔍 Session processed user structure:', { 
          id: combinedUser.id, 
          email: combinedUser.email, 
          role: combinedUser.role,
          hasId: !!combinedUser.id,
          userKeys: Object.keys(combinedUser)
        });
        setCurrentUser(combinedUser);
        
        // Check if there's a pending team creation in localStorage
        const pendingTeamData = localStorage.getItem('pendingTeamData');
        if (pendingTeamData) {
          log.info('🏒 Found pending team creation data');
          // Team creation will be handled by the teams page
        }
        
        return combinedUser;
      }
    } catch (error) {
      log.error('❌ [v5] Error processing user session:', error);
      // If we have existing user data, preserve it
      if (existingUser) {
        log.warn('⚠️ [v5] Keeping existing user data due to error');
        return existingUser;
      }
      setCurrentUser(session.user);
      return session.user;
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        log.info('🚀 [v5] Checking for existing session...');

        // Check for PKCE code in URL (cross-browser password reset / email confirmation)
        const urlParams = new URLSearchParams(window.location.search);
        const pkceCode = urlParams.get('code');

        if (pkceCode) {
          log.info('🔐 [v5] PKCE code detected in URL, exchanging for session...');

          // Set flag to ignore SIGNED_OUT event during session exchange
          setIsProcessingEmailConfirmation(true);

          try {
            // Exchange the PKCE code for a session
            // Supabase will automatically handle the code verifier from localStorage
            const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(pkceCode);

            if (exchangeError) {
              log.error('❌ [v5] Error exchanging PKCE code:', exchangeError);

              // PKCE validation failed - likely because the link was opened in a different browser
              // The code verifier is stored in localStorage of the original browser
              if (exchangeError.code === 'validation_failed' || exchangeError.status === 400) {
                log.warn('⚠️ [v5] PKCE validation failed - likely cross-browser scenario');

                // Clear the code from URL
                window.history.replaceState(null, '', window.location.pathname);

                // Show helpful error message
                setCurrentView('login');
                setIsLoading(false);
                setAuthInitialized(true);

                // Display error message to user
                setTimeout(() => {
                  alert('Password reset link opened in a different browser.\n\nPlease use the "Forgot Password" link on this page to request a new reset email, or open the link in the same browser where you requested the password reset.');
                }, 500);

                return;
              }

              // Clear the code from URL even on other errors
              window.history.replaceState(null, '', window.location.pathname);
            } else if (data?.session) {
              log.info('✅ [v5] Session established from PKCE code for:', data.session.user.email);

              // Clear the code from URL
              window.history.replaceState(null, '', window.location.pathname);

              // Clear all cached data when signing in
              clearStaleCache();

              const userData = await processUserSession(data.session);
              if (userData) {
                // For password recovery, set hash BEFORE rendering dashboard so useAppState picks it up
                // PKCE code flow is typically used for cross-browser scenarios
                log.info('🔐 [v5] PKCE flow (likely password recovery) - setting hash to indicate account tab');
                window.location.hash = '#account';

                setCurrentView('dashboard');
              }
              setIsLoading(false);
              setAuthInitialized(true);
              return;
            }
          } catch (err) {
            log.error('❌ [v5] Error processing PKCE code:', err);
            // Clear the code from URL
            window.history.replaceState(null, '', window.location.pathname);
          }
        }

        // Check for auth tokens in URL hash (password recovery and email confirmation - same browser)
        // For PKCE flow, we need to manually extract and set the session from hash fragments
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          // Handle double hash scenario (#reset-password#access_token=...)
          // Extract the part after the last # that contains the tokens
          const hashParts = hash.split('#');
          const tokenHash = hashParts.find(part => part.includes('access_token')) || '';
          const hashParams = new URLSearchParams(tokenHash);
          const type = hashParams.get('type');
          log.info(`🔐 [v5] Auth tokens detected in URL hash (type: ${type}), processing manually...`);

          // Set flag to ignore SIGNED_OUT event during setSession
          setIsProcessingEmailConfirmation(true);

          // Extract tokens from hash
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          if (accessToken) {
            try {
              // Set the session using the tokens from the hash
              const { data: { session: hashSession }, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || ''
              });

              if (sessionError) {
                log.error('❌ [v5] Error setting session from hash:', sessionError);
              } else if (hashSession) {
                log.info('✅ [v5] Session established from URL hash for:', hashSession.user.email);

                // Clear all cached data when signing in
                clearStaleCache();

                const userData = await processUserSession(hashSession);
                if (userData) {
                  // For password recovery, set hash to account tab, otherwise clear it
                  if (type === 'recovery') {
                    log.info('🔐 [v5] Password recovery - setting hash to indicate account tab');
                    window.location.hash = '#account';
                  } else {
                    // Clear the hash from URL for non-recovery auth
                    window.history.replaceState(null, '', window.location.pathname + window.location.search);
                  }

                  setCurrentView('dashboard');
                }
                setIsLoading(false);
                setAuthInitialized(true);
                return;
              }
            } catch (err) {
              log.error('❌ [v5] Error processing hash tokens:', err);
            }
          }
        }

        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          log.error('❌ [v5] Error getting session:', error);
          setCurrentView('login');
        } else if (session) {
          log.info('✅ [v5] Found existing session for:', session.user.email);

          // Check if this is a returning session after being away (stale cache scenario)
          const cacheSize = queryClient.getQueryCache().getAll().length;
          if (cacheSize === 0) {
            log.info('🗑️ [v5] No cached data found - clearing cache for fresh start');
            clearStaleCache();
          }

          // Process the existing session
          const userData = await processUserSession(session);
          if (userData) {
            setCurrentView('dashboard');
          }
        } else {
          log.info('ℹ️ [v5] No existing session found');
          setCurrentView('login');
        }

        setIsLoading(false);
        setAuthInitialized(true);
      } catch (err) {
      // Error caught and ignored
        log.error('❌ [v5] Error during session check:', err);
        setIsLoading(false);
        setAuthInitialized(true);
        setCurrentView('login');
      }
    };
    
    checkInitialSession();
  }, [processUserSession, clearStaleCache, queryClient]);

  // Listen for auth state changes - set up EARLY to catch email confirmation events
  useEffect(() => {
    log.info('🎯 [v5] Setting up auth state listener');

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      log.info(`🔔 [v5] Auth event: ${event}`, session?.user?.email || 'no session');

      try {
        if (event === 'SIGNED_IN') {
          // Process SIGNED_IN event (includes email confirmations and regular logins)
          if (session?.user) {
            log.info('🔐 [v5] Processing SIGNED_IN event for:', session.user.email);

            // IMPORTANT: Don't process session during initial auth check
            // The checkInitialSession effect will handle it
            if (!authInitialized) {
              log.info('⏭️ [v5] Skipping SIGNED_IN during initialization - will be handled by checkInitialSession');
              return;
            }

            // Clear all cached data when signing in to prevent stale data
            clearStaleCache();

            // Pass existing currentUser to preserve role if there's a transient error
            const userData = await processUserSession(session, currentUser);
            if (userData) {
              setCurrentView('dashboard');
            }

            // Ensure auth is marked as initialized
            setIsLoading(false);

            // Clear email confirmation flag if it was set
            setIsProcessingEmailConfirmation(false);
          }
        } else if (event === 'SIGNED_OUT') {
          // Ignore SIGNED_OUT during email confirmation processing
          // (setSession triggers SIGNED_OUT before SIGNED_IN)
          if (isProcessingEmailConfirmation) {
            log.info('⏭️ [v5] Ignoring SIGNED_OUT during email confirmation processing');
            return;
          }

          log.info('👋 [v5] User signed out or session expired');

          // Clear all cached data when signing out
          clearStaleCache();

          setCurrentUser(null);
          setCurrentView('login');

          // Force redirect to login page to ensure user sees login screen
          window.location.hash = '';
          if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
          }
        } else if (event === 'TOKEN_REFRESHED') {
          log.info('🔄 [v5] Token refreshed successfully for:', session?.user?.email);
        }
      } catch (error) {
        log.error('❌ [v5] Error handling auth state change:', error);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [processUserSession, clearStaleCache]);

  // Periodic session validation to catch silent session expiration
  useEffect(() => {
    if (!authInitialized || !currentUser) {
      return;
    }

    // Check session validity every 60 seconds
    const sessionCheckInterval = setInterval(async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          log.warn('🔍 [v4] Session check failed - session expired or invalid:', error?.message || 'no session');
          
          // Session is invalid, trigger logout
          clearStaleCache();
          setCurrentUser(null);
          setCurrentView('login');
          
          // Force redirect to login page
          window.location.hash = '';
          if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
          }
        } else {
          log.debug('✅ [v4] Session check passed');
        }
      } catch (error) {
        log.error('❌ [v4] Error during session check:', error);
        
        // On error, assume session is invalid
        clearStaleCache();
        setCurrentUser(null);
        setCurrentView('login');
      }
    }, 60000); // Check every 60 seconds

    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, [authInitialized, currentUser, clearStaleCache]);

  // Handle session expiration from session manager
  useEffect(() => {
    const handleSessionExpired = () => {
      log.warn('🔒 [v4] Session manager detected session expiration');
      
      // Clear cached data
      clearStaleCache();
      
      // Update auth state
      setCurrentUser(null);
      setCurrentView('login');
      
      // Force redirect to login page
      window.location.hash = '';
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
      }
    };

    sessionManager.addSessionExpiredListener(handleSessionExpired);

    return () => {
      sessionManager.removeSessionExpiredListener(handleSessionExpired);
    };
  }, [clearStaleCache]);

  // User management functions
  const handleRegistrationSuccess = (user) => {
    log.info('Registration successful for:', user.email);
    log.info('🔍 User from registration has email_confirmed_at:', user.email_confirmed_at);
    log.info('🔍 User from registration structure:', { 
      id: user.id, 
      email: user.email, 
      role: user.role,
      hasId: !!user.id,
      userKeys: Object.keys(user)
    });
    // Set the user directly - edge function now includes email_confirmed_at
    setCurrentUser(user);
    setCurrentView('dashboard');
  };

  const updateCurrentUser = async (userId) => {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Error fetching updated user data:', error);
        // Update the current user with default data
        setCurrentUser(prev => ({ ...prev, ...{} }));
      } else if (userData) {
        log.info('User data fetched successfully');
        // Update the current user with fetched data
        setCurrentUser(prev => ({ ...prev, ...userData }));
        
        // Check if there's a pending team creation in localStorage
        const pendingTeamData = localStorage.getItem('pendingTeamData');
        if (pendingTeamData) {
          log.info('🏒 Found pending team creation data after user update');
        }
      }
    } catch (error) {
      log.error('Error updating current user:', error);
    }
  };

  const handleAccountUpdateSuccess = (updatedUser) => {
    try {
      // Combine auth user with database user data
      setCurrentUser({ ...currentUser, ...updatedUser });
      setCurrentView('dashboard');
      log.info('Account updated successfully');
    } catch (error) {
      log.error('Error updating account:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setCurrentUser(null);
      setCurrentView('login');
    } catch (err) {
      // Error caught and ignored
      log.error('Error logging out:', err);
    }
  };

  return {
    // State
    currentUser,
    currentView,
    isLoading,
    authInitialized,
    
    // Actions
    setCurrentView,
    setCurrentUser,
    handleRegistrationSuccess,
    updateCurrentUser,
    handleAccountUpdateSuccess,
    handleLogout,
    
    // Utilities
    processUserSession
  };
};